import * as React from "react";
import { Box, Button, FormControl, Grid, IconButton, Link, Stack, TextField, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { BriefDocumentKind, UploadedBriefFile, useSubmitCampaignBrief } from "@/hooks/api/use-campaign-briefs";
import { useRateCard } from "@/hooks/api/use-rate-card";
import { useAuth } from "@/components/core/auth-provider";
import { RouterLink } from "@/components/core/link";
import { CustomDatePicker } from "@/components/customDatePicker/custom-date-picker";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { SchedulePicker } from "@/components/schedule/schedule-picker";

import { formatAmount } from "../../rate-card/format";
import { amountOf, estimateRequest, PRICING } from "../estimate";
import {
	BriefFormErrors,
	BriefFormValues,
	emptyBriefForm,
	filesToUpload,
	toBriefPayload,
	validateBriefForm,
} from "../form";
import { AgentsSection } from "./agents-section";
import { EstimatePanel } from "./estimate-panel";
import { FileRow } from "./file-row";
import { MarketsSection } from "./markets-section";
import { SectionCard } from "./section-card";

/** The section each error belongs to, so a failed submit can scroll to the first one to fix. */
const SECTION_OF: [RegExp, string][] = [
	[/^title$/, "campaign"],
	[/^(dialDate|schedule|redials\.)/, "when"],
	[/^markets$/, "markets"],
	[/^(agentChoice|agentId|extraAgents\.)/, "agents"],
	[/^(call_flow|lead_list|po|sow|poRequired|sowRequired)$/, "files"],
];
const SECTION_ORDER = ["campaign", "when", "markets", "agents", "files"];

/**
 * "Request a campaign": the client says what they want run and sees what it comes to as they go,
 * like a basket. Saving and uploading happen on one click; if anything fails, the saved draft is
 * kept and the next click carries on from there rather than starting a second request.
 */
export function NewCampaignRequest(): React.JSX.Element {
	const navigate = useNavigate();
	const { user, account } = useAuth();
	const submit = useSubmitCampaignBrief();
	const { data: card, isError: cardError } = useRateCard();

	const [values, setValues] = React.useState<BriefFormValues>(emptyBriefForm);
	const [errors, setErrors] = React.useState<BriefFormErrors>({});
	const [draftId, setDraftId] = React.useState<string | null>(null);
	const [uploaded, setUploaded] = React.useState<Partial<Record<BriefDocumentKind, UploadedBriefFile>>>({});
	const [step, setStep] = React.useState<string | null>(null);

	const set = <K extends keyof BriefFormValues>(key: K, value: BriefFormValues[K]) => {
		setValues((current) => ({ ...current, [key]: value }));
		setErrors(({ [key]: _, ...rest }) => rest);
	};

	const setFile = (kind: BriefDocumentKind, file: File | null) => {
		setValues((current) => {
			const files = { ...current.files };
			if (file) files[kind] = file;
			else delete files[kind];
			return { ...current, files };
		});
		setErrors(({ [kind]: _, ...rest }) => rest);
	};

	const estimate = React.useMemo(
		() =>
			card
				? estimateRequest(
						{
							agentChoice: values.agentChoice,
							markets: values.markets,
							extraAgents: values.extraAgents,
							addOns: values.addOns,
						},
						card
					)
				: null,
		[card, values.agentChoice, values.markets, values.extraAgents, values.addOns]
	);
	const currency = estimate?.currency ?? "SGD";
	const extraAgentAmount = card ? amountOf(card, PRICING.extraAgent) : null;

	// Tint the line that just changed, the way a basket shows what was added.
	const [freshKey, setFreshKey] = React.useState<string | null>(null);
	const previousLines = React.useRef<Map<string, string> | null>(null);
	React.useEffect(() => {
		if (!estimate) return;
		const now = new Map(estimate.lines.map((line) => [line.key, `${line.label}|${line.amount}`]));
		const before = previousLines.current;
		previousLines.current = now;
		const changed = before && estimate.lines.find((line) => before.get(line.key) !== now.get(line.key));
		if (!changed) return;
		setFreshKey(changed.key);
		const timer = setTimeout(() => setFreshKey(null), 1600);
		return () => clearTimeout(timer);
	}, [estimate]);

	const handleSubmit = async () => {
		const found = validateBriefForm(values, dayjs().format("YYYY-MM-DD"));
		setErrors(found);
		if (Object.keys(found).length > 0) {
			const sections = new Set(Object.keys(found).map((key) => SECTION_OF.find(([pattern]) => pattern.test(key))?.[1]));
			const first = SECTION_ORDER.find((section) => sections.has(section));
			if (first) document.querySelector(`#${first}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
			return;
		}

		try {
			const brief = await submit.mutateAsync({
				draftId,
				payload: toBriefPayload(values),
				files: filesToUpload(values),
				uploaded,
				onDraft: setDraftId,
				onUploaded: (kind, file) =>
					setUploaded(({ [kind]: _, ...current }) => (file ? { ...current, [kind]: file } : current)),
				onStep: setStep,
			});
			navigate(`/campaign-briefs/${brief.id}`);
		} catch {
			// The toast says what went wrong; the draft and anything uploaded are kept for the retry.
			setStep(null);
		}
	};

	const errorFor = (key: string) => (errors[key] ? <CustomFormHelperError text={errors[key]!} /> : null);

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack spacing={1}>
					<Link
						color="text.primary"
						component={RouterLink}
						href="/campaign-briefs"
						sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
						variant="subtitle2"
					>
						<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
						Campaign requests
					</Link>
					<Typography variant="h4">Request a campaign</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
						Tell us what you would like to run. Your estimate updates as you add markets, agents and services, and we
						confirm the quote with you before anything is charged.
						{user?.name && account?.name ? ` Sending as ${user.name} · ${account.name}.` : ""}
					</Typography>
				</Stack>

				<Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
					<Grid size={{ xs: 12, lg: 8 }}>
						<Stack spacing={3}>
							<SectionCard id="campaign" step={1} title="The campaign" description="A name your team will recognise.">
								<FormControl error={Boolean(errors.title)} fullWidth>
									<TextField
										label="Campaign name"
										required
										value={values.title}
										onChange={(event) => set("title", event.target.value)}
										placeholder="Q4 renewals"
										error={Boolean(errors.title)}
									/>
									{errorFor("title")}
								</FormControl>
							</SectionCard>

							<SectionCard
								id="when"
								step={2}
								title="When to dial"
								description="Times are local to each market: 09:00 is 09:00 in Singapore and 09:00 in Jakarta."
							>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, sm: 6 }}>
										<FormControl error={Boolean(errors.dialDate)} fullWidth>
											<CustomDatePicker
												label="First dialling day"
												value={values.dialDate}
												minDate={dayjs()}
												setValue={(date) => set("dialDate", date)}
												error={Boolean(errors.dialDate)}
											/>
											{errorFor("dialDate")}
										</FormControl>
									</Grid>
									{values.redialDates.map((date, index) => (
										<Grid key={index} size={{ xs: 12, sm: 6 }}>
											<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
												<CustomDatePicker
													label={`Redial ${index + 1}`}
													value={date || null}
													minDate={values.dialDate ? dayjs(values.dialDate).add(1, "day") : dayjs()}
													setValue={(picked) => {
														set(
															"redialDates",
															values.redialDates.map((current, at) => (at === index ? (picked ?? "") : current))
														);
														setErrors(({ [`redials.${index}`]: _, ...rest }) => rest);
													}}
													error={Boolean(errors[`redials.${index}`])}
												/>
												<IconButton
													aria-label={`Remove redial ${index + 1}`}
													onClick={() => {
														set(
															"redialDates",
															values.redialDates.filter((_, at) => at !== index)
														);
														setErrors(({ ...rest }) =>
															Object.fromEntries(Object.entries(rest).filter(([key]) => !key.startsWith("redials.")))
														);
													}}
												>
													<TrashIcon />
												</IconButton>
											</Stack>
											{errorFor(`redials.${index}`)}
										</Grid>
									))}
								</Grid>
								<Box>
									<Button
										size="small"
										startIcon={<PlusIcon />}
										// The API takes at most 20 redial days.
										disabled={values.redialDates.length >= 20}
										onClick={() => set("redialDates", [...values.redialDates, ""])}
									>
										Add a redial day
									</Button>
								</Box>
								<Stack spacing={1}>
									<Typography variant="subtitle2">Dialling hours on each day</Typography>
									<Typography variant="body2" color="text.secondary">
										Leave gaps for breaks, for example 09:00–12:00 and 13:00–17:00.
									</Typography>
									<Box
										sx={{
											p: 2,
											border: "1px solid",
											borderColor: errors.schedule ? "error.main" : "var(--mui-palette-divider)",
											borderRadius: 1.5,
										}}
									>
										<SchedulePicker
											value={values.schedule}
											onChange={(slots) => {
												set("schedule", slots);
												setErrors(({ dialDate: _, ...rest }) =>
													Object.fromEntries(Object.entries(rest).filter(([key]) => !key.startsWith("redials.")))
												);
											}}
											showSummary
										/>
									</Box>
									{errorFor("schedule")}
								</Stack>
							</SectionCard>

							<MarketsSection
								step={3}
								value={values.markets}
								onChange={(markets) => set("markets", markets)}
								card={card}
								currency={currency}
								error={errors.markets}
							/>

							<AgentsSection
								step={4}
								choice={values.agentChoice}
								onChoice={(choice) => {
									set("agentChoice", choice);
									setErrors(({ agentId: _, ...rest }) => rest);
								}}
								agent={{ id: values.agentId, name: values.agentName }}
								onAgent={({ id, name }) => {
									setValues((current) => ({ ...current, agentId: id, agentName: name }));
									setErrors(({ agentId: _, ...rest }) => rest);
								}}
								extraAgents={values.extraAgents}
								onExtraAgents={(agents) => {
									set("extraAgents", agents);
									setErrors((current) =>
										Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith("extraAgents.")))
									);
								}}
								extraAgentPrice={extraAgentAmount === null ? null : formatAmount(currency, extraAgentAmount)}
								errors={errors}
							/>

							<SectionCard id="files" step={5} title="Files and paperwork" description="Up to 25 MB each.">
								<Stack spacing={1.5}>
									{values.agentChoice === "new" ? (
										<FileRow
											kind="call_flow"
											note="needed for the new agent"
											file={values.files.call_flow}
											onChange={(file) => setFile("call_flow", file)}
											error={errors.call_flow}
										/>
									) : null}
									<FileRow
										kind="lead_list"
										file={values.files.lead_list}
										onChange={(file) => setFile("lead_list", file)}
										error={errors.lead_list}
									/>
									<FileRow
										kind="po"
										note="optional"
										file={values.files.po}
										onChange={(file) => setFile("po", file)}
										error={errors.po}
										required={values.poRequired}
										onRequiredChange={(required) => set("poRequired", required)}
										requiredError={errors.poRequired}
									/>
									<FileRow
										kind="sow"
										note="optional"
										file={values.files.sow}
										onChange={(file) => setFile("sow", file)}
										error={errors.sow}
										required={values.sowRequired}
										onRequiredChange={(required) => set("sowRequired", required)}
										requiredError={errors.sowRequired}
									/>
								</Stack>
								<TextField
									label="Notes for our team"
									multiline
									minRows={3}
									value={values.notes}
									onChange={(event) => set("notes", event.target.value)}
									placeholder="Anything we should know: blackout dates, tone, who to escalate to"
								/>
							</SectionCard>
						</Stack>
					</Grid>

					<Grid size={{ xs: 12, lg: 4 }} sx={{ position: { lg: "sticky" }, top: { lg: 80 } }}>
						<EstimatePanel
							estimate={estimate}
							card={card}
							cardError={cardError}
							freshKey={freshKey}
							submitting={submit.isPending}
							step={step}
							draftSaved={Boolean(draftId)}
							hasErrors={Object.keys(errors).length > 0}
							onSubmit={handleSubmit}
						/>
					</Grid>
				</Grid>
			</Stack>
		</Box>
	);
}
